/* eslint-disable react-hooks/exhaustive-deps */
import { useAuthStore } from '@/store'
import { IPermission, PermissionTypes } from '@/types'
import React, { ReactNode, createContext, useCallback, useContext, useMemo } from 'react'

// Define the shape of the context
interface UserPermissionsContextType {
  permissions: IPermission[]
  actions: PermissionTypes[]
  hasPermission: (...action: PermissionTypes[]) => boolean
}

// Create the context
const UserPermissionsContext = createContext<UserPermissionsContextType | undefined>(undefined)

// Define the provider props type
interface UserPermissionsProviderProps {
  children: ReactNode
}

// Function to check action with wildcard support
const checkAction = (patterns: PermissionTypes[], actions: PermissionTypes[]): boolean => {
  if (actions.includes('all_access')) return true
  return actions.some((action) => {
    return patterns.some((pattern) => {
      const regexPattern = pattern.replace(/([.+?^=!:${}()|[\]\\])/g, '\\$1').replace(/\*/g, '.*')
      const regex = new RegExp(`^${regexPattern}$`)
      return regex.test(action)
    })
  })
}

// Create the provider component
export const UserPermissionsProvider: React.FC<UserPermissionsProviderProps> = ({ children }) => {
  const { user } = useAuthStore()

  const computePermissions = () => {
    const grantedPermissions = user?.accessGranted?.flatMap((el) => el.attachedPolicies || []) || []
    const directPermissions = user?.accessDirectPermissions || []
    const permissions = [...grantedPermissions, ...directPermissions].reduce<Array<IPermission>>((acc, permission) => {
      if (!acc.some((item) => item.action === permission.action)) acc.push(permission)
      return acc
    }, [])
    return permissions
  }

  const permissions = useMemo(computePermissions, [JSON.stringify(user || null)])
  const actions = useMemo(() => permissions.map((el) => el.action), [JSON.stringify(permissions)])

  const hasPermission = useCallback(
    // (...actions: PermissionTypes[]) => {
    //   if (!actions.length) return true // No permission required
    //   return checkAction(actions, actions)
    // },
    (...requestedActions: PermissionTypes[]) => {
      if (!requestedActions.length) return true // No permission required
      if (actions.includes('all_access')) return true // User has all_access permission
      return checkAction(requestedActions, actions) // Compare requested actions against user's actual permissions
    },
    [actions],
  )

  return <UserPermissionsContext.Provider value={{ permissions, actions, hasPermission }}>{children}</UserPermissionsContext.Provider>
}

// Create a custom hook to use the UserPermissionsContext
export const useUserPermissions = (): UserPermissionsContextType => {
  const context = useContext(UserPermissionsContext)
  if (context === undefined) {
    throw new Error('useUserPermissions must be used within a UserPermissionsProvider')
  }
  return context
}

// Create a custom hook to use the UserPermissionsContext
export const useHasPermission = (): UserPermissionsContextType['hasPermission'] => {
  const context = useContext(UserPermissionsContext)
  if (context === undefined) {
    throw new Error('useUserPermissions must be used within a UserPermissionsProvider')
  }
  return context.hasPermission
}
